import { HttpBackend, HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { BrowserStorageService } from './common/browser-storage.service';
import { ApiCallsService } from './network/api-calls.service';

interface Coordinates {
  lat: number;
  lng: number;
}

@Injectable({ providedIn: 'root' })
export class LocationService {
  private locationSubject = new BehaviorSubject<Coordinates | null>(null);
  location$ = this.locationSubject.asObservable();
  apiKey: any = 'AIzaSyDU8ZVFpCFlmWrEcbdsmekkKtY6wuSESjY';

  constructor(
    private http: HttpClient,
    private _handler: HttpBackend,
    private _apiService: ApiCallsService,
    private _storageService: BrowserStorageService,
  ) {
    if (this._storageService.getBrowser()) {
      const storedLocation = localStorage.getItem('userLocation');
      if (storedLocation) {
        this.locationSubject.next(JSON.parse(storedLocation));
      }
    }
  }

  async fetchLocation(): Promise<void> {
    try {
      const location = await this.getBrowserLocation();
      this.setLocation(location);
    } catch (_) {
      const fallback = await this.getIPLocation();
      this.setLocation(fallback);
    }
  }

  setLocation(coords: Coordinates): void {
    localStorage.setItem('userLocation', JSON.stringify(coords));
    this.locationSubject.next(coords);
  }

  getBrowserLocation(): Promise<Coordinates> {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) return reject('Geolocation not supported');
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        (err) => reject(err),
      );
    });
  }

  getIPLocation(): Promise<Coordinates> {
    return fetch('https://ipapi.co/json/')
      .then((res) => res.json())
      .then((data) => {
        return {
          lat: data.latitude,
          lng: data.longitude,
          data: JSON.stringify(data),
          name: data.city || '' + ',' + data.region || '' + data.country_name || '',
        };
      });
  }

  getLocationName(lat: any, lng: any): Promise<string> {
    return new Promise((resolve, reject) => {
      const geocoder = new google.maps.Geocoder();
      const latlng = { lat, lng };

      geocoder.geocode({ location: latlng }, (results, status) => {
        if (status === 'OK' && results && results.length > 0) {
          // You can pick a different result type if needed
          resolve(results[0].formatted_address);
        } else {
          reject('Geocoder failed due to: ' + status);
        }
      });
    });
  }

  getCurrentLocation(): Promise<{ lat: number; lng: number }> {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject('Geolocation is not supported by your browser.');
      } else {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            resolve({
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            });
          },
          (error) => {
            if (error.code === error.PERMISSION_DENIED) {
              this._apiService?.errorAlert('Location access denied. Please enable location services.', false, '', error);
            } else if (error.code === error.POSITION_UNAVAILABLE) {
              this._apiService?.errorAlert('Location information is unavailable.', false, '', error);
            } else if (error.code === error.TIMEOUT) {
              this._apiService?.errorAlert('The request to get your location timed out.', false, '', error);
            } else {
              this._apiService?.errorAlert('An unknown error occurred while fetching location.', false, '', error);
            }
          },
          { enableHighAccuracy: true },
        );
      }
    });
  }
}
